/**
 * Exclude ARCHIVED dimension items from a list, except keep `currentValue`
 * if it points at an archived item — so an already-saved selection stays
 * visible in an edit row until the user changes it.
 *
 * Reports and admin tree are intentional callers of unfiltered data.
 *
 * @param {Array<{id, state}>} items
 * @param {string|number|null|undefined} currentValue id of the row's current selection
 */
export function filterActiveDimensionItems(items, currentValue) {
  if (!Array.isArray(items)) return [];
  const currentId =
    currentValue !== null && currentValue !== undefined && currentValue !== ""
      ? currentValue.toString()
      : null;
  return items.filter(
    (item) => item?.state !== "ARCHIVED" || (currentId !== null && item?.id?.toString() === currentId)
  );
}

/**
 * De-duplicate dimension items by their `name`, keeping the first occurrence.
 * Entries with a null/undefined `name` are dropped.
 *
 * Dedup is BY NAME (not id) on purpose: the org can hold multiple `dimension_item`
 * rows sharing a name within one dimension (e.g. an ACTIVE + an ARCHIVED "Germany",
 * or two ACTIVE items after a bad import). Insights drill-downs seed their rows
 * from the org-structure catalogue and then join emissions BY NAME, so same-name
 * twins would each render a row resolving to the identical value. Deduping by id
 * would NOT merge them. We also intentionally do NOT filter ARCHIVED here — the
 * active+active case shows that's insufficient, and name-dedup covers every case.
 *
 * @param {Array<{name?: string}>} items
 * @returns {Array} first occurrence per distinct name; [] for non-array input
 */
export function dedupeByName(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    const name = item?.name;
    if (name == null || seen.has(name)) return false;
    return seen.add(name); // Set.add returns the Set (truthy) → keep first occurrence
  });
}
